import { useEffect, useState } from 'react'

import { useGetFeatureFlagsQuery } from 'shared/api/containerApi/requests/getFeatureFlags'
import { LocalStorageKey } from 'shared/constants/localStorage'
import {
  resetFeatureFlags,
  setFeatureFlags
} from 'shared/services/featureFlags'
import { logger } from 'shared/services/logger'
import { UserWithToken } from 'shared/types/user'
import { isUserAnonymous } from 'shared/utils/isUserAnonymous'
import { lsGetUserItem, lsSetUserItem } from 'shared/utils/local-storage'

import { readParentFeatureFlags } from '../utils/readParentFeatureFlags'

/**
 * Loads the user's feature flags once per app load and publishes them for
 * remotes. Cached flags are published right away and refreshed in the
 * background, so remotes only wait for the very first fetch
 * (see `whenFeatureFlagsReady`).
 */
export const useLoadFeatureFlags = (user: UserWithToken | null) => {
  // Same rule as /apps: anonymous and external-token sessions would only 401.
  const canFetch = !!user && !isUserAnonymous(user) && !user.isExternalAuth
  // Org switching reloads the page as the same user, so the org is part of the key.
  const cacheKey = canFetch ? `${user.id}:${user.organization_id}` : undefined
  const [parentFlags] = useState(readParentFeatureFlags)

  const { data, error } = useGetFeatureFlagsQuery(
    parentFlags ? undefined : cacheKey
  )

  useEffect(() => {
    if (!cacheKey) {
      setFeatureFlags({})
      return
    }

    const cached =
      parentFlags ??
      lsGetUserItem(LocalStorageKey.FEATURE_FLAGS_CACHE, cacheKey)

    if (cached) setFeatureFlags(cached)
    else resetFeatureFlags()
  }, [cacheKey, parentFlags])

  useEffect(() => {
    if (!data || !cacheKey) return
    setFeatureFlags(data)
    try {
      lsSetUserItem(LocalStorageKey.FEATURE_FLAGS_CACHE, cacheKey, data)
    } catch {
      // The cache is best-effort: a full localStorage must not crash the app.
    }
  }, [data, cacheKey])

  useEffect(() => {
    if (!error) return
    // Intentional: tells developers why flags are off.
    // eslint-disable-next-line no-console
    console.error(
      '[feature-flags] Failed to load feature flags. Using cached flags if any, otherwise all feature flags are inactive.',
      error
    )
    logger.exception('feature_flags.load_failed', error)
    setFeatureFlags(window.__CONTAINER_FEATURE_FLAGS__ ?? {})
  }, [error])
}
